'use client';

import { FC, ReactNode, useState, useEffect, useMemo, type JSX } from 'react';

import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';

import { getEnv, useBaseAuth, useUpdateEffect } from 'shared/base';

import { FeatureFlagsContext } from './context';

import { FeatureFlag, localFeatureFlagValues } from '../../constants';

interface Props {
  children: ReactNode;
  clientIdOverride?: string;
  customAttributes?: {
    isCQInternal?: boolean;
    isPlatform?: boolean;
  };
  isPlatform?: boolean;
  teamNameOverride?: string;
}

const clientId = process.env.NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_ID;

const LOCAL_STORAGE_KEYS = {
  OVERRIDE_ACCESS: 'feature-flags:override-access',
  OVERRIDE_VALUES: 'feature-flags:override-values',
};

export const FeatureFlagsProvider: FC<Props> = ({
  children,
  clientIdOverride,
  customAttributes,
  teamNameOverride,
}) => {
  const { currentMembership, user } = useBaseAuth();
  const teamName = teamNameOverride ?? currentMembership?.team?.name;
  const userEmail = user?.email;
  const userEmailDomain = userEmail?.split('@')[1];
  const userKey = user?.id;
  const [LDProvider, setLDProvider] =
    useState<({ children }: { children: ReactNode }) => JSX.Element>();
  const LDClientId = clientIdOverride || clientId;

  const [sessionFeatureFlags, setSessionFeatureFlags] = useState<
    Partial<typeof localFeatureFlagValues>
  >(() => {
    if (typeof window === 'undefined') {
      return {};
    }

    const stored = window.localStorage.getItem(LOCAL_STORAGE_KEYS.OVERRIDE_VALUES);
    if (stored) {
      return JSON.parse(stored) as Partial<typeof localFeatureFlagValues>;
    }

    return {};
  });

  const allowedEditingFeatureFlags = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.localStorage.getItem(LOCAL_STORAGE_KEYS.OVERRIDE_ACCESS) === 'true',
    [],
  );

  useEffect(() => {
    if (
      LDClientId &&
      getEnv(
        'NEXT_PUBLIC_DISABLE_FEATURE_FLAGS',
        process.env.NEXT_PUBLIC_DISABLE_FEATURE_FLAGS,
        'false',
      ) !== 'true'
    ) {
      const init = async () => {
        const wrapper = await asyncWithLDProvider({
          clientSideID: LDClientId,
          context: userEmail
            ? {
                custom: {
                  email_domain: userEmailDomain,
                  teamName,
                  ...customAttributes,
                },
                key: userKey,
                kind: 'user',
                team: teamName,
              }
            : undefined,
          reactOptions: {
            useCamelCaseFlagKeys: false,
          },
        });

        setLDProvider(() => wrapper);
      };

      init();
    }
  }, [teamName, userEmail, userEmailDomain, userKey, customAttributes, LDClientId]);

  useUpdateEffect(() => {
    window.localStorage.setItem(
      LOCAL_STORAGE_KEYS.OVERRIDE_VALUES,
      JSON.stringify(sessionFeatureFlags),
    );
  }, [sessionFeatureFlags]);

  return (
    <FeatureFlagsContext.Provider
      value={{
        allowedEditingFeatureFlags,
        featureFlags: localFeatureFlagValues,
        featureFlagsToOverride: sessionFeatureFlags,
        resetFeatureFlagLocally: (key: FeatureFlag) => {
          setSessionFeatureFlags((prev) => {
            const { [key]: _, ...rest } = prev;

            return rest;
          });
        },
        setFeatureFlagLocally: (key, value) => {
          setSessionFeatureFlags((prev) => ({ ...prev, [key]: value }));
        },
      }}
    >
      {LDProvider ? <LDProvider>{children}</LDProvider> : children}
    </FeatureFlagsContext.Provider>
  );
};
